/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import uuidv4 from 'uuid/v4';

import { useTranslation } from 'react-i18next';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Dialog from 'components/DialogWrapper/DialogWrapper';

import Divider from '@material-ui/core/Divider';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Button from 'components/Button/Button';
import DialogHeader from 'components/DialogHeader/DialogHeader';

import useStyles from './MapDialogLayoutStyles';

import { useLoadScript } from '@react-google-maps/api';
import Map from './components/Map';

import {
  mdiMap,
  mdiRedo,
  mdiUndo,
  mdiVectorSquarePlus,
  mdiVectorSquareRemove,
} from '@mdi/js';
import Icon from '@mdi/react';
import useCircles from './hooks/useCircles';
import useMarkers from './hooks/useMarkers';
import usePolygons from './hooks/usePolygons';

const normalizeVertices = (positions) =>
  positions.map((pos) => ({
    latitude: Number(pos.latitude),
    longitude: Number(pos.longitude),
  }));

const seedDrawnAreas = (defaultAreas, defaultArea) => {
  if (Array.isArray(defaultAreas) && defaultAreas.length > 0) {
    return defaultAreas
      .filter((area) => Array.isArray(area?.positions))
      .map((area) => ({
        id: area.id || uuidv4(),
        positions: normalizeVertices(area.positions),
      }));
  }
  // legacy single-area prop: flat vertex array -> one area
  if (Array.isArray(defaultArea) && defaultArea.length > 0) {
    return [{ id: uuidv4(), positions: normalizeVertices(defaultArea) }];
  }
  return [];
};

function MapDialogLayout(props) {
  const {
    projects,
    records,
    onClose,
    mapType,

    selectPositionMode,
    handleSelectPosition,
    defaultSelectedPosition,

    drawCircleMode,
    defaultRadius,
    handleCircleRadius,

    drawAreaMode,
    defaultAreas,
    handleDrawAreas,
    defaultArea,
    handleDrawArea,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation('common');
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const [selectedPosition, setSelectedPosition] = useState(
    defaultSelectedPosition
  );

  const [circleRadius, setCircleRadius] = useState(defaultRadius || 0);

  // Undo/redo history of the drawing session. Each entry is a consistent
  // snapshot; the history lives only in this dialog's state, so closing
  // the dialog discards it. Geometry changes push entries, selection-only
  // changes rewrite the current entry.
  const [history, setHistory] = useState(() => [
    {
      drawnAreas: seedDrawnAreas(defaultAreas, defaultArea),
      // null -> the next map click starts a new area
      activeAreaId: null,
    },
  ]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // "Add new area" arms drawing: the next click starts the new area even
  // when it lands inside an existing area's fill (which would otherwise
  // select that area). Not part of the undo history.
  const [newAreaArmed, setNewAreaArmed] = useState(false);

  const { drawnAreas, activeAreaId } = history[historyIndex];

  const activeArea = drawnAreas.find((area) => area.id === activeAreaId);
  const completeAreas = drawnAreas.filter((area) => area.positions.length >= 3);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const commitAreas = (nextDrawnAreas, nextActiveAreaId) => {
    setHistory((prev) => [
      ...prev.slice(0, historyIndex + 1),
      { drawnAreas: nextDrawnAreas, activeAreaId: nextActiveAreaId },
    ]);
    setHistoryIndex(historyIndex + 1);
  };

  const replaceSelection = (nextActiveAreaId) => {
    setHistory((prev) =>
      prev.map((entry, index) =>
        index === historyIndex
          ? { ...entry, activeAreaId: nextActiveAreaId }
          : entry
      )
    );
  };

  const handleUndo = () => {
    setHistoryIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleRedo = () => {
    setHistoryIndex((prev) => Math.min(prev + 1, history.length - 1));
  };

  useEffect(() => {
    if (!drawAreaMode) return undefined;
    const handleKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      const key = event.key.toLowerCase();
      if (key !== 'z' && key !== 'y') return;
      event.preventDefault();
      if (key === 'y' || (key === 'z' && event.shiftKey)) {
        setHistoryIndex((prev) => Math.min(prev + 1, history.length - 1));
      } else {
        setHistoryIndex((prev) => Math.max(prev - 1, 0));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [drawAreaMode, history.length]);

  const handleAddAreaVertex = (vertex) => {
    setNewAreaArmed(false);
    if (!activeAreaId) {
      const newId = uuidv4();
      commitAreas([...drawnAreas, { id: newId, positions: [vertex] }], newId);
      return;
    }
    commitAreas(
      drawnAreas.map((area) =>
        area.id === activeAreaId
          ? { ...area, positions: [...area.positions, vertex] }
          : area
      ),
      activeAreaId
    );
  };

  // Double-clicking a vertex handle of the active area deletes that vertex.
  // Deleting the last remaining vertex removes the whole area.
  const handleDeleteAreaVertex = (vertexIndex) => {
    if (!activeArea) return;
    if (vertexIndex < 0 || vertexIndex >= activeArea.positions.length) return;
    const nextPositions = activeArea.positions.filter(
      (position, index) => index !== vertexIndex
    );
    if (nextPositions.length === 0) {
      commitAreas(
        drawnAreas.filter((area) => area.id !== activeAreaId),
        null
      );
      return;
    }
    commitAreas(
      drawnAreas.map((area) =>
        area.id === activeAreaId ? { ...area, positions: nextPositions } : area
      ),
      activeAreaId
    );
  };

  const handleActiveAreaPathChanged = (positions) => {
    if (!activeAreaId) return;
    const nextDrawnAreas = drawnAreas.map((area) =>
      area.id === activeAreaId ? { ...area, positions } : area
    );
    // polygon mouseup also fires without an actual edit - skip no-op entries
    if (JSON.stringify(nextDrawnAreas) === JSON.stringify(drawnAreas)) return;
    commitAreas(nextDrawnAreas, activeAreaId);
  };

  const handleSelectArea = (areaId) => {
    setNewAreaArmed(false);
    if (areaId === activeAreaId) return;
    // an incomplete (<3 vertex) active area is dropped on deselect
    const nextDrawnAreas = drawnAreas.filter(
      (area) => area.id === areaId || area.positions.length >= 3
    );
    if (nextDrawnAreas.length !== drawnAreas.length) {
      commitAreas(nextDrawnAreas, areaId);
    } else {
      replaceSelection(areaId);
    }
  };

  // A click on an inactive drawn area: while a new area is armed or the
  // active area is still incomplete the click places a vertex (a clickable
  // polygon swallows the map's own click event); otherwise it selects the
  // clicked area for editing.
  const handleDrawnAreaClick = (areaId, vertex) => {
    if (newAreaArmed || (activeArea && activeArea.positions.length < 3)) {
      handleAddAreaVertex(vertex);
      return;
    }
    handleSelectArea(areaId);
  };

  const handleAddNewArea = () => {
    replaceSelection(null);
    setNewAreaArmed(true);
  };

  const handleDeleteArea = () => {
    if (!activeAreaId) return;
    commitAreas(
      drawnAreas.filter((area) => area.id !== activeAreaId),
      null
    );
  };

  const handleSelect = () => {
    selectPositionMode && handleSelectPosition(selectedPosition);
    drawCircleMode && handleCircleRadius(Math.round(circleRadius));
    if (drawAreaMode) {
      const areas = completeAreas.map((area) => ({
        id: area.id,
        positions: normalizeVertices(area.positions),
      }));
      if (handleDrawAreas) {
        handleDrawAreas(areas);
      } else if (handleDrawArea) {
        handleDrawArea(areas[0]?.positions || []);
      }
    }
    onClose();
  };

  const { allMarkers } = useMarkers({
    records,
    projects,
    selectedPosition,
    selectPositionMode,
  });

  const { allCircles } = useCircles({
    projects,
    selectedPosition,
    circleRadius,
  });

  const { allPolygons } = usePolygons({
    projects,
    drawAreaMode,
    drawnAreas,
    activeAreaId,
  });

  const upSm = useMediaQuery(theme.breakpoints.up('sm'));
  return (
    <Dialog
      aria-labelledby="contact-edit-dialog"
      classes={{ paper: classes.dialogPaper }}
      disableBackdropClick
      fullScreen={!upSm}
      fullWidth
      maxWidth="sm"
      open={true}
      onClose={onClose}
    >
      <DialogHeader
        icon={<Icon path={mdiMap} size={1} className={classes.icon} />}
        onClose={onClose}
        title={t('location.map')}
      />

      <DialogContent className={classes.dialogContent}>
        {isLoaded && (
          <Map
            mapType={mapType}
            markers={allMarkers}
            circles={allCircles}
            polygons={allPolygons}
            selectPositionMode={selectPositionMode}
            setSelectedPosition={setSelectedPosition}
            drawCircleMode={drawCircleMode}
            setCircleRadius={setCircleRadius}
            drawAreaMode={drawAreaMode}
            addAreaVertex={handleAddAreaVertex}
            updateActiveAreaPositions={handleActiveAreaPathChanged}
            onDrawnAreaClick={handleDrawnAreaClick}
            deleteAreaVertex={handleDeleteAreaVertex}
          />
        )}
      </DialogContent>

      <Divider />
      <DialogActions className={classes.dialogActions}>
        <Button
          className={classes.actionBtn}
          onClick={onClose}
          color="dark"
          outline
        >
          Sulje
        </Button>
        {drawAreaMode && (
          <Button
            className={classes.actionBtn}
            onClick={handleUndo}
            color="dark"
            outline
            disabled={!canUndo}
            title={t('location.undo')}
            aria-label={t('location.undo')}
          >
            <Icon path={mdiUndo} size={1} />
          </Button>
        )}
        {drawAreaMode && (
          <Button
            className={classes.actionBtn}
            onClick={handleRedo}
            color="dark"
            outline
            disabled={!canRedo}
            title={t('location.redo')}
            aria-label={t('location.redo')}
          >
            <Icon path={mdiRedo} size={1} />
          </Button>
        )}
        {drawAreaMode && (
          <Button
            className={classes.actionBtn}
            onClick={handleDeleteArea}
            color="danger"
            disabled={!activeAreaId}
          >
            <Icon path={mdiVectorSquareRemove} size={1} />
            {t('location.delete_area')}
          </Button>
        )}
        {drawAreaMode && (
          <Button
            className={classes.actionBtn}
            onClick={handleAddNewArea}
            color="dark"
            disabled={
              newAreaArmed || (activeArea && activeArea.positions.length < 3)
            }
          >
            <Icon path={mdiVectorSquarePlus} size={1} />
            {t('location.add_new_area')}
          </Button>
        )}
        {(selectPositionMode || drawAreaMode || drawCircleMode) && (
          <Button
            className={classes.actionBtn}
            onClick={() => handleSelect()}
            color="dark"
            disabled={
              !selectedPosition || (drawAreaMode && completeAreas.length === 0)
            }
          >
            {t('common.save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

MapDialogLayout.defaultProps = {};

MapDialogLayout.propTypes = {
  onClose: PropTypes.func.isRequired,
  records: PropTypes.array,
  projects: PropTypes.array,
  mapType: PropTypes.string,
};

export default MapDialogLayout;
